import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import AccountDefaultLogo from 'assets/images/account-avatar.svg';
import { Spinner } from 'components/loadings';
import Notification from 'components/notification';
import { SessionContext } from 'contexts/session';
import { useCustomContentAvailabilityContext } from 'contexts/customContentAvailability';
import useCustomContentList from 'hooks/useCustomContentList';
import useCustomContentMetrics from 'hooks/useCustomContentMetrics';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import { updateCustomContentVisibility } from 'services/customContent';
import type { ICustomContentVisibility } from 'services/customContent';
import { CustomContentCard } from './components';
import { normalizeCustomContentItems, normalizeCustomContentType } from './utils';
import { CardsList, EmptyState, PageContent } from './CustomContent.styles';
import {
  AccountAvatar,
  Container,
  HeaderBanner,
  HeaderContent,
  HeaderSubtitle,
  HeaderTextBlock,
  HeaderTitle,
} from './styles';

const CustomContent = (): JSX.Element => {
  const session = useContext(SessionContext);
  const { account } = session;
  const customContentEnabled = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED);
  const customContentEnabledM3 = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED_M3);
  const isOwner = Boolean(session?.isAccountOwner);
  const isTeamManager = Boolean(session?.isTeamManager);
  const canViewVisibility = Boolean(
    customContentEnabled && customContentEnabledM3 && (isOwner || isTeamManager)
  );
  const canManageVisibility = Boolean(customContentEnabled && customContentEnabledM3 && isOwner);

  const { hasCustomContent, isCheckingCustomContent } = useCustomContentAvailabilityContext();
  const { items, fetching, hasFetchError } = useCustomContentList();
  const [localItems, setLocalItems] = useState(items);
  const [pendingById, setPendingById] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const normalizedItems = useMemo(() => normalizeCustomContentItems(localItems), [localItems]);
  const metricsContentIds = useMemo(
    () =>
      normalizedItems
        .map((item) => item.rawItem?.content_id || item.rawItem?.id || '')
        .filter(Boolean),
    [normalizedItems]
  );
  const metricsEnabled = Boolean(customContentEnabledM3 && hasCustomContent && !fetching);

  const { metricsById, metricsLoading, certificationNameByContentId } = useCustomContentMetrics({
    enabled: metricsEnabled,
    customContentIds: metricsContentIds,
  });

  const accountName = account?.name || 'Account';

  const getMetricsTitle = (item: typeof normalizedItems[number]): string | undefined => {
    if (normalizeCustomContentType(item.rawItem?.content_type) !== 'learning_path_with_cert') {
      return undefined;
    }

    const metricsId = item.rawItem?.content_id || item.rawItem?.id || '';
    const certificationName = certificationNameByContentId[metricsId];

    return certificationName || 'Certification';
  };

  const onVisibilityChange = useCallback(
    async (id: string, visibility: ICustomContentVisibility) => {
      if (!canManageVisibility) return;
      setPendingById((previous) => ({ ...previous, [id]: true }));
      try {
        const response = await updateCustomContentVisibility(id, { visibility });
        setLocalItems((previousItems) =>
          previousItems.map((item) =>
            item.id === id ? { ...item, visibility: response.data.visibility } : item
          )
        );
      } catch (error) {
        Notification({ text: 'There was a problem updating content visibility.', type: 'error' });
      } finally {
        setPendingById((previous) => ({ ...previous, [id]: false }));
      }
    },
    [canManageVisibility]
  );

  if (isCheckingCustomContent) {
    return <Spinner active size="50" />;
  }

  if (!hasCustomContent) {
    return <Redirect to="/" />;
  }

  return (
    <Container>
      <HeaderBanner>
        <HeaderContent>
          <AccountAvatar
            src={account?.logo_url || AccountDefaultLogo}
            alt={`${accountName} logo`}
          />
          <HeaderTextBlock>
            <HeaderTitle>{`${accountName} Custom Training`}</HeaderTitle>
            <HeaderSubtitle>
              Verified training resources created for your organization
            </HeaderSubtitle>
          </HeaderTextBlock>
        </HeaderContent>
      </HeaderBanner>
      <PageContent>
        <CardsList>
          {fetching && <CustomContentCard loading />}

          {!fetching &&
            normalizedItems.map((item) => {
              const metricsId = item.rawItem?.content_id || item.rawItem?.id || '';

              return (
                <CustomContentCard
                  key={item.id}
                  item={item}
                  metrics={metricsById[metricsId] || []}
                  metricsLoading={metricsLoading}
                  showMetrics
                  metricsTitle={getMetricsTitle(item)}
                  canViewVisibility={canViewVisibility}
                  canManageVisibility={canManageVisibility}
                  onVisibilityChange={onVisibilityChange}
                  isVisibilityPending={Boolean(pendingById[item.id])}
                />
              );
            })}

          {!fetching && hasFetchError && normalizedItems.length === 0 && (
            <EmptyState>There was a problem loading custom training content.</EmptyState>
          )}
        </CardsList>
      </PageContent>
    </Container>
  );
};

export default CustomContent;
